/**
 * components/object/ObjectHealthPanel.jsx — Iter 53.
 *
 * Compact, drawing-first health summary for the Overview tab. Shows:
 *   - Health pill with calm vocabulary
 *   - Top 4 reasons (clickable → jumps to source)
 *   - Offline indicator + "Last known" timestamp when cached
 *
 * NO dashboard. NO heat map. NO canvas badge. Pure inspector content.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertTriangle, CloudOff, ChevronRight } from "lucide-react";
import {
  fetchObjectHealth, healthLabel, healthTone,
} from "@/lib/objectHealth";
import { dispatchJump } from "@/lib/jumpTo";

const MAX_REASONS = 4;

export default function ObjectHealthPanel({ objectType, objectId, refreshKey }) {
  const [state, setState] = useState({
    loading: true, online: true, data: null,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const res = await fetchObjectHealth(objectType, objectId);
    setState({ loading: false, online: res.online, data: res.data });
  }, [objectType, objectId]);

  useEffect(() => {
    if (!objectType || !objectId) return;
    load();
  }, [objectType, objectId, refreshKey, load]);

  if (state.loading && !state.data) {
    return (
      <div className="fop-surface p-3 rounded-md" data-testid="object-health-panel"
            data-state="loading">
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2 flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Health
        </div>
      </div>
    );
  }

  if (!state.data) {
    return (
      <div className="fop-surface p-3 rounded-md" data-testid="object-health-panel"
            data-state="unknown">
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-1">
          Health
        </div>
        <div className="text-[11px] text-[var(--fop-text-3)]">
          Unable to load health.
        </div>
      </div>
    );
  }

  const h = state.data;
  const tone = healthTone(h.health_state);
  const top = (h.reasons || []).slice(0, MAX_REASONS);
  const fetchedAgo = h._fetched_at ? Math.round((Date.now() - h._fetched_at) / 1000) : null;

  return (
    <div className="fop-surface p-3 rounded-md" data-testid="object-health-panel"
          data-state={h.health_state}
          data-reasons={h.reason_count || 0}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)]">
          Health
        </div>
        {!state.online && (
          <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[#f59e0b]"
                 data-testid="object-health-offline"
                 title={fetchedAgo ? `Last known ${fetchedAgo}s ago` : ""}>
            <CloudOff size={9} /> Offline
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="px-2 py-0.5 rounded-full text-[11px] font-semibold font-mono uppercase tracking-wider"
          style={{
            color: tone,
            backgroundColor: tone + "22",
            border: `1px solid ${tone}55`,
          }}
          data-testid="object-health-pill"
        >
          {healthLabel(h.health_state)}
        </span>
        {h.reason_count > 0 && (
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
                 data-testid="object-health-reason-count">
            {h.reason_count} reason{h.reason_count === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {top.length > 0 && (
        <ul className="space-y-1" data-testid="object-health-reasons">
          {top.map((r, idx) => (
            <ReasonRow key={`${r.kind}-${idx}`} reason={r} />
          ))}
          {h.reasons.length > top.length && (
            <li className="text-[10px] font-mono text-[var(--fop-text-3)] px-1">
              +{h.reasons.length - top.length} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function ReasonRow({ reason }) {
  const tone = healthTone(reason.health_state);
  const jt = reason.jump_target;
  const isJumpable = !!(jt && jt.object_type && jt.object_id);
  return (
    <li
      className={`text-[11px] flex items-start gap-1.5 px-1.5 py-1 rounded transition-colors ${isJumpable ? "cursor-pointer hover:bg-[var(--fop-surface-2)]" : ""}`}
      onClick={() => {
        if (!isJumpable) return;
        dispatchJump({
          object_type: jt.object_type, object_id: jt.object_id,
          target_tab: jt.target_tab,
          focus_issue_id: jt.focus_issue_id,
          focus_document_id: jt.focus_document_id,
        });
        // Switch the same-inspector tab without a jump round-trip.
        if (jt.target_tab) {
          window.dispatchEvent(new CustomEvent("forgedops:inspector-tab", {
            detail: {
              object_type: jt.object_type, object_id: jt.object_id,
              tab: jt.target_tab,
            },
          }));
        }
      }}
      data-testid={`object-health-reason-${reason.kind}`}
      data-reason-kind={reason.kind}
      data-severity={reason.severity}
    >
      <span className="mt-0.5 shrink-0 inline-block w-1.5 h-1.5 rounded-full"
             style={{ backgroundColor: tone }} aria-hidden />
      <span className="flex-1 text-[var(--fop-text-2)] leading-snug truncate">
        {reason.message}
      </span>
      {isJumpable && (
        <ChevronRight size={10} className="text-[var(--fop-text-3)] shrink-0 mt-0.5" />
      )}
    </li>
  );
}
